import {css, useTheme} from '@mui/material';
import {
  ConfigObjectType,
  DlMuiButton,
  enumContainer,
  GetStylesFunction,
  IconHeaderWithSearch,
  LightSplitLayout,
  LoadingSpinner,
  MaximizeButton,
  omitNullsy,
  ResultCount,
  useDefaultScrollTo,
  useLocalStorage,
  useLocationQuery,
  useMaximizeWrapper,
} from '@wallaby-ui/core';
import {
  AlertBoardChart,
  AlertBoardChartBreakdown,
  AlertBoardChartType,
  AlertBoardChartValues,
  AlertBoardKpiStrip,
  AlertBoardRowDto,
  AlertCategory,
  AlertCategoryIcon,
  AlertDefinitionTooltip,
  AlertWeightBadge,
  ALERT_BOARD_CHART_BREAKDOWN_ENUM,
  ALERT_BOARD_CHART_TYPE_ENUM,
  ALERT_BOARD_CHART_VALUES_ENUM,
  ALERT_BOARD_DEFAULT_SORT_PROPERTY,
  ALERT_BOARD_SORT_PROPERTIES,
  ALERT_CATEGORY_ENUM,
  ALERT_EMPTY_VALUE,
  ALERT_ORIGIN_ENUM,
  BOOL_FILTER_OPTIONS,
  countSelectedFilters,
  DEFAULT_ALERT_BOARD_CHART_BREAKDOWN,
  DEFAULT_ALERT_BOARD_CHART_TYPE,
  DEFAULT_ALERT_BOARD_CHART_VALUES,
  DEFAULT_PAGE_SIZE,
  formatAlertAvgDays,
  formatAlertSolvedRate,
  getDefaultMTOptions,
  getSingleBoolFilterValue,
  LightFilterPanel,
  LightMonthRangeFilter,
  LightNumberRangeFilter,
  LightSelectedFilters,
  LightSelectedFiltersPane,
  LightSelectFilter,
  MaterialTableWrapper,
  TableSorting,
  useAlertBoard,
  useAlertBoardChart,
  useFormatters,
  usePermissions,
  UserFilterDto,
  useUserFilters,
} from '@wallaby-ui/document';
import {isEqual} from 'lodash';
import {Column} from 'material-table';
import * as React from 'react';
import {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import {Helmet} from 'react-helmet';
import {useIntl} from 'react-intl';
import {Link, useNavigate} from 'react-router-dom';

import {
  ALERT_BOARD_COMPONENT,
  AlertBoardPageFilters,
  alertBoardTableFiltersForChips,
  buildAlertBoardChartParams,
  buildAlertBoardParams,
  buildAlertBoardSelectedFilters,
  buildAlertBoardShowAllQuery,
  DEFAULT_ALERT_BOARD_FILTERS,
  DEFAULT_ALERT_BOARD_SORTING,
  normalizeAlertBoardFiltersForCompare,
  resolveBoardFirstActiveMonths,
} from './alertBoardFilters';
import {AlertOverflowText} from './AlertOverflowText';
import {ALERT_LIST_ROUTE_PATH} from './alertRoutes';
import {withServerSort} from './alertTableSort';
import {useMerieAlertCriteriaRenderer} from './MerieAlertCriteriaChips';
import {useAlertBoardAlertOptions} from './useAlertBoardAlertOptions';
import {useAlertExport} from './useAlertExport';
import {useAppSelector} from 'src/js/store_hooks';

const SPLIT_GUTTER_SIZE = 18;
/** The maximize wrapper's padding of a card, the saved filters' and the table's. */
const CARD_PADDING_X = 16;
/** The design's chart card padding inside the maximize wrapper, the title keeping its own gap below. */
const CHART_CARD_PADDING_X = 24;
const CHART_CARD_INNER_STYLE: React.CSSProperties = {padding: `20px ${CHART_CARD_PADDING_X}px 22px`, gap: 0};
/**
 * The initial height of the strip and the table: the first 5 rows show. The strip (106) and its gap (16), the card's
 * padding and header (56), the column header (47), 5 dense rows (44 each), the record count with its gap (8 + 23) and
 * the card's bottom padding (16).
 */
const BOARD_PANE_HEIGHT = 106 + 16 + 56 + 47 + 5 * 44 + 8 + 23 + 16;
/** The initial height of the dynamics chart's card. */
const CHART_PANE_HEIGHT = 400;
/** The filter card's usual height, two and a half rows of filters, which the split fits on the first visit. */
const FILTERS_PANE_HEIGHT = 180;
/**
 * The lower pane: the strip and the table above, the dynamics chart below, the user moving the gutter between them.
 * The sizes are the panes' heights at the page's minimum height; a taller window grows both in proportion.
 */
const ALERT_BOARD_CHART_SPLIT_CONFIG = {
  sizes: [BOARD_PANE_HEIGHT, CHART_PANE_HEIGHT],
  minSize: [240, 220],
  // v2: the sizes became heights (PDW-416), the ratios stored before would not show the 5 rows
  layoutId: '/LightSplitLayout.AlertBoardLayout.chart.v2',
};
const BOARD_TABLE_OPTIONS = {...getDefaultMTOptions(DEFAULT_PAGE_SIZE), paging: false};
const ALERT_BOARD_SPLIT_CONFIG = {
  sizes: [FILTERS_PANE_HEIGHT, BOARD_PANE_HEIGHT + SPLIT_GUTTER_SIZE + CHART_PANE_HEIGHT],
  // the lower pane never gets less than both of its own panes need, so it never scrolls
  minSize: [64, ALERT_BOARD_CHART_SPLIT_CONFIG.minSize[0] + SPLIT_GUTTER_SIZE + ALERT_BOARD_CHART_SPLIT_CONFIG.minSize[1]],
  layoutId: '/LightSplitLayout.AlertBoardLayout.v2',
};
/**
 * The page is never lower than the filters, the 5 rows and the chart need: a lower window scrolls the whole page, as
 * the reports do, never a pane.
 */
const ALERT_BOARD_MIN_HEIGHT = ALERT_BOARD_SPLIT_CONFIG.sizes[0] + SPLIT_GUTTER_SIZE + ALERT_BOARD_SPLIT_CONFIG.sizes[1];

type Props = {
  config: ConfigObjectType;
};

type FiltersData = {
  boardOpen: boolean;
  board: AlertBoardPageFilters;
  boardSorting: TableSorting;
};

/**
 * The alert board (PDW-184 / FE PDW-246): whole-board KPIs over the alerts the viewer may see, then one row per alert
 * definition with its counts, and below them the dynamics chart (PDW-249) in its own pane. Core filters, counts and
 * sorts, and answers every alert at once, which the table scrolls (PDW-413); the page only maps its filter to the query
 * string, the chart's read taking the same filter.
 * Reachable only with the Use alerts right, which the navigation and the BE both require.
 */
export const AlertBoardLayout: React.FC<Props> = (props) => {
  const {config} = props;
  const component = ALERT_BOARD_COMPONENT;
  const storageIdPrefix = '/alertBoard';
  const [query, changeFilters] = useLocationQuery(storageIdPrefix, true);
  const filters: FiltersData = useMemo(
    () => ({
      boardOpen: query.boardOpen ?? true,
      board: query?.board ?? DEFAULT_ALERT_BOARD_FILTERS,
      boardSorting: query?.boardSorting ?? DEFAULT_ALERT_BOARD_SORTING,
    }),
    [query]
  );
  const {waitForUserFilters, userFiltersLoading, addedUserFilters, userFilters, changeUserFilter} = useUserFilters({
    storageIdPrefix,
    component,
    config,
    changeFilters: (newFilters) => changeFilters({board: newFilters}),
  });
  if (waitForUserFilters) return <LoadingSpinner isVisible marginTop={16} />;
  return (
    <AlertBoardLayoutInner
      {...props}
      storageIdPrefix={storageIdPrefix}
      component={component}
      userFiltersLoading={userFiltersLoading}
      addedUserFilters={addedUserFilters}
      userFilters={userFilters}
      changeUserFilter={changeUserFilter}
      filters={filters}
      changeFilters={changeFilters}
    />
  );
};

type InnerProps = Props & {
  storageIdPrefix: string;
  component: string;
  userFiltersLoading: boolean;
  addedUserFilters: UserFilterDto[];
  userFilters: UserFilterDto[];
  changeUserFilter: (newUserFilter: UserFilterDto, toDelete?: boolean) => void;
  filters: FiltersData;
  changeFilters: (filterDiff: Partial<FiltersData>) => void;
};

/** One table column per design column; the History column is not shown until the alert history views exist. */
function useColumns(config: ConfigObjectType, board: AlertBoardPageFilters): Column<AlertBoardRowDto>[] {
  const intl = useIntl();
  const {t, tEnum} = useFormatters();
  const navigate = useNavigate();
  const renderCriteria = useMerieAlertCriteriaRenderer(config);
  return useMemo(() => {
    // an alert's name leads to the alerts list as "Show all" does, the alert filter holding only that alert (PDW-444)
    const listHref = (row: AlertBoardRowDto) =>
      ALERT_LIST_ROUTE_PATH && `${ALERT_LIST_ROUTE_PATH}?${buildAlertBoardShowAllQuery({...board, userFilterIds: [row.userFilterId]})}`;
    const retired = (row: AlertBoardRowDto) => row.alertState === 'RETIRED';
    const avgDays = (row: AlertBoardRowDto) => {
      const value = formatAlertAvgDays(row.avgDaysToSolve, intl);
      return value == null ? ALERT_EMPTY_VALUE : intl.formatMessage({id: 'alert.board.days'}, {days: value});
    };
    const columns: Column<AlertBoardRowDto>[] = [
      {
        field: 'isGlobal',
        title: '',
        sorting: false,
        width: '44px',
        cellStyle: {textAlign: 'center'},
        render: (row) => (
          <span css={styles.category} data-retired={retired(row)}>
            <AlertCategoryIcon isGlobal={row.isGlobal} fontSize={12} />
          </span>
        ),
      },
      {
        field: 'name',
        title: t('alert.board.column.alert'),
        sorting: false,
        render: (row) => {
          const href = listHref(row);
          const nameProps = {'css': styles.name, 'data-retired': retired(row), 'data-name': 'dl-alert-board-name'};
          return (
            <AlertDefinitionTooltip filterComponent={row.component} filterData={row.data} renderCriteria={renderCriteria}>
              {href ? (
                <a
                  {...nameProps}
                  href={href}
                  onClick={(e) => {
                    e.preventDefault();
                    navigate(href);
                  }}
                >
                  {row.name}
                </a>
              ) : (
                <span {...nameProps}>{row.name}</span>
              )}
            </AlertDefinitionTooltip>
          );
        },
      },
      {
        field: 'weight',
        title: t('alert.weight'),
        // the design's 80px holds the label but not the sort arrow beside it ('Weight' needs 94px)
        width: '96px',
        render: (row) => <AlertWeightBadge weight={row.weight} variant='board' />,
      },
      {
        field: 'component',
        title: t('hai.properties.origin'),
        sorting: false,
        render: (row) => (
          <AlertOverflowText text={tEnum(ALERT_ORIGIN_ENUM, row.component)} textCss={styles.muted} data-retired={retired(row)} />
        ),
      },
      {
        field: 'activePatients',
        title: t('alert.board.column.activePatients'),
        width: '92px',
        render: (row) => (
          <span css={styles.count} data-retired={retired(row)}>
            {intl.formatNumber(row.activePatients)}
          </span>
        ),
      },
      {
        field: 'activeEpisodes',
        title: t('alert.board.column.activeEpisodes'),
        width: '92px',
        render: (row) => (
          <span css={styles.count} data-retired={retired(row)}>
            {intl.formatNumber(row.activeEpisodes)}
          </span>
        ),
      },
      {
        field: 'solvedRate',
        title: t('alert.board.column.solvedRate'),
        width: '110px',
        render: (row) => (
          <span css={styles.muted} data-retired={retired(row)}>
            {formatAlertSolvedRate(row.solvedRate, intl)}
          </span>
        ),
      },
      {
        field: 'avgDaysToSolve',
        title: t('alert.board.column.avgTimeToSolve'),
        width: '130px',
        render: (row) => (
          <span css={styles.muted} data-retired={retired(row)}>
            {avgDays(row)}
          </span>
        ),
      },
    ];
    // MERIE sorts the rows (retired definitions last by default): the table keeps the server's order and only shows the
    // sort on the header, else the default Weight indicator would re-sort the rows here
    return columns.map((v) => ({...v, customSort: () => 0}));
  }, [intl, t, tEnum, navigate, renderCriteria, board]);
}

const AlertBoardLayoutInner: React.FC<InnerProps> = (props) => {
  const {config, storageIdPrefix, component, userFiltersLoading, addedUserFilters, userFilters, changeUserFilter, filters, changeFilters} =
    props;
  const theme = useTheme();
  const muiStyles = getStyles({theme});
  const formatters = useFormatters();
  const {t} = formatters;
  const {isLoading: permissionsLoading, userRoles} = usePermissions(config);
  const canUseAlerts = Boolean(userRoles.ALERT_USE_ROLE);
  const {board} = filters;
  const hasFilters = !isEqual(omitNullsy(normalizeAlertBoardFiltersForCompare(board)), omitNullsy(DEFAULT_ALERT_BOARD_FILTERS));

  // A floating range ("Current month") is resolved again for today, since MERIE takes the dates only, and any range is
  // widened to whole months, the days of a filter saved while the board picked days included
  const floating = resolveBoardFirstActiveMonths(board);
  useEffect(() => {
    if (floating && (floating.from !== board.firstActiveFrom || floating.to !== board.firstActiveTo)) {
      changeFilters({board: {...board, firstActiveFrom: floating.from, firstActiveTo: floating.to}});
    }
  }, [floating?.from, floating?.to]); // eslint-disable-line react-hooks/exhaustive-deps
  const floatingPending = Boolean(floating && (floating.from !== board.firstActiveFrom || floating.to !== board.firstActiveTo));

  const params = useMemo(() => buildAlertBoardParams(board, filters.boardSorting), [board, filters.boardSorting]);
  const {board: data, isLoading} = useAlertBoard(config, params, canUseAlerts && !floatingPending);
  const definitions = data?.definitions;
  // the file: the strip, every definition in the table's order and the export info, the chart not part of it; it has
  // no row limit, as the table has no page (PDW-413), so there is no limit warning
  const {handleXlsxExport, isExporting} = useAlertExport({url: config.service.url.alertBoardExport, params});
  const chartParams = useMemo(() => buildAlertBoardChartParams(board), [board]);
  const {chart, isFetching: chartFetching} = useAlertBoardChart(config, chartParams, canUseAlerts && !floatingPending);
  const enumMap = useAppSelector((state) => state[enumContainer.ENUM_REDUCER_STATE_ID].enumMap);

  const {options: alertOptions, isLoading: alertOptionsLoading, getAlertLabel} = useAlertBoardAlertOptions(config, canUseAlerts);
  const columns = useColumns(config, board);

  const filterInputsRef = useRef<HTMLDivElement>(null);
  const filtersRef = useRef<HTMLDivElement | null>(null);
  // the table and the chart each cover the whole page when maximized, as the reports' and the lists' cards
  const {
    maximized: tableMaximized,
    toggleMaximized: toggleTableMaximized,
    MaximizeWrapper: TableMaximizeWrapper,
  } = useMaximizeWrapper('dl-alert-board-table-wrapper');
  const {
    maximized: chartMaximized,
    toggleMaximized: toggleChartMaximized,
    MaximizeWrapper: ChartMaximizeWrapper,
  } = useMaximizeWrapper('dl-alert-board-chart-wrapper', {innerStyle: CHART_CARD_INNER_STYLE});
  // Fullscreen filter panel (PDM-2842): this wrapper IS the panel's card (16px padding, no bottom padding so the
  // content scrolls to the card edge); the panel itself owns the button and keeps the flag consistent with "open".
  const {
    maximized: filterPanelMaximized,
    setMaximized: setFilterPanelMaximized,
    MaximizeWrapper: FilterPanelMaximizeWrapper,
  } = useMaximizeWrapper('filter-panel-wrapper', {innerStyle: {rowGap: 16, paddingBottom: 0}});
  // The saved filters' maximize button sits the filter panel's scrollbar width further in (LightFilterPanel mirrors
  // it in its header), so the table's and the chart's buttons follow it and the three stay in one column
  const [filtersScrollbarWidth, setFiltersScrollbarWidth] = useState(0);
  useEffect(() => {
    const node = filtersRef.current;
    if (!node || typeof ResizeObserver === 'undefined') {
      setFiltersScrollbarWidth(0);
      return undefined;
    }
    const update = () => setFiltersScrollbarWidth(node.offsetWidth - node.clientWidth);
    update();
    const observer = new ResizeObserver(update);
    observer.observe(node);
    return () => observer.disconnect();
  }, [permissionsLoading, canUseAlerts, filters.boardOpen]);

  const selectedFilters = buildAlertBoardSelectedFilters(board, formatters, {getAlertLabel});
  const tableFiltersForChips = alertBoardTableFiltersForChips(board);
  const selectedFiltersCount = countSelectedFilters(selectedFilters, tableFiltersForChips);

  // the page's scroll is kept between visits, as on the reports
  const [defaultScroll, setDefaultScroll] = useLocalStorage(`${storageIdPrefix}.scroll`, 0);
  const scrollWrapperRef = useDefaultScrollTo(defaultScroll);
  const onScroll = useCallback(
    (event: React.UIEvent<HTMLDivElement>) => setDefaultScroll(event.currentTarget.scrollTop),
    [setDefaultScroll]
  );

  if (permissionsLoading) return <LoadingSpinner isVisible text='' />;
  if (!canUseAlerts) return null;

  const changeBoard = (newBoard: AlertBoardPageFilters) => changeFilters({board: newBoard});
  // a chart option leaves the table alone
  const changeChartOption = (option: Partial<AlertBoardPageFilters>) => changeFilters({board: {...board, ...option}});
  const categoryOptions = enumMap[ALERT_CATEGORY_ENUM] ?? [];
  const originOptions = enumMap[ALERT_ORIGIN_ENUM] ?? [];
  // With retired alerts left out the default order is weight descending, which the Weight header shows
  const shownSort: TableSorting =
    filters.boardSorting.field && filters.boardSorting.direction
      ? filters.boardSorting
      : {field: ALERT_BOARD_DEFAULT_SORT_PROPERTY, direction: 'desc'};
  const tableColumns = withServerSort(columns, shownSort);

  return (
    <div css={muiStyles.contentWrapper} ref={scrollWrapperRef} onScroll={onScroll}>
      <Helmet title={t('navigation.route.alertBoard')} />
      <LightSplitLayout
        initSizes={ALERT_BOARD_SPLIT_CONFIG.sizes}
        minSizes={ALERT_BOARD_SPLIT_CONFIG.minSize}
        storageId={ALERT_BOARD_SPLIT_CONFIG.layoutId}
        isVertical
        gutterSize={SPLIT_GUTTER_SIZE}
        noGrow={[!filters.boardOpen, false]}
        autoFitFilterRows={{contentRef: filterInputsRef, enabled: filters.boardOpen}}
        hidden={[false, false]}
        style={{minHeight: ALERT_BOARD_MIN_HEIGHT}}
      >
        <FilterPanelMaximizeWrapper>
          <LightFilterPanel
            open={filters.boardOpen}
            toggleOpen={() => changeFilters({boardOpen: !filters.boardOpen})}
            hasActiveFilter={hasFilters}
            scrollAreaRef={filtersRef}
            filterInputsRef={filterInputsRef}
            savedFiltersProps={{
              storageIdPrefix,
              component,
              userFiltersLoading,
              addedUserFilters,
              userFilters,
              changeUserFilter,
              filter: board,
              setFilter: (newFilter) => changeFilters({board: newFilter}),
              config,
              normalizeForCompare: normalizeAlertBoardFiltersForCompare,
            }}
            selectedFilters={
              selectedFiltersCount.total > 0 ? (
                <LightSelectedFiltersPane
                  selectedFilters={selectedFilters}
                  tableFilters={tableFiltersForChips}
                  changeTableFilters={(newTableFilters) => changeBoard(newTableFilters as AlertBoardPageFilters)}
                  formatters={formatters}
                />
              ) : null
            }
            selectedFiltersCount={selectedFiltersCount.total}
            maximize={{maximized: filterPanelMaximized, setMaximized: setFilterPanelMaximized}}
          >
            <LightMonthRangeFilter
              config={{name: 'firstActive', ui: {label: 'alert.board.filter.dateRange'}}}
              value={
                board.firstActiveFrom && board.firstActiveTo
                  ? {startDate: board.firstActiveFrom, endDate: board.firstActiveTo, shortcut: board.firstActiveFloatingShortcutName}
                  : null
              }
              onChange={(newValue) =>
                changeBoard({
                  ...board,
                  firstActiveFrom: newValue?.startDate,
                  firstActiveTo: newValue?.endDate,
                  firstActiveFloatingShortcutName: newValue?.shortcut,
                })
              }
              showShortcuts
            />
            <LightSelectFilter
              config={{name: 'userFilterIds', ui: {label: 'alert.board.filter.alert'}}}
              options={alertOptions}
              loading={alertOptionsLoading}
              multi
              value={board.userFilterIds?.map(String)}
              onChange={(newValue: string[] | null) =>
                changeBoard({...board, userFilterIds: newValue?.length ? newValue.map(Number) : undefined})
              }
            />
            <LightSelectFilter
              config={{name: 'categories', ui: {label: 'alert.board.filter.category'}}}
              options={categoryOptions}
              multi
              maxWidth={'176px'}
              value={board.categories}
              onChange={(newValue: AlertCategory[] | null) => changeBoard({...board, categories: newValue?.length ? newValue : undefined})}
            />
            <LightNumberRangeFilter
              config={{name: 'weight', ui: {label: 'alert.weight', min: 0, max: 100}}}
              value={board.weightFrom != null && board.weightTo != null ? {start: board.weightFrom, end: board.weightTo} : null}
              onChange={(newValue) => changeBoard({...board, weightFrom: newValue?.start, weightTo: newValue?.end})}
            />
            <LightSelectFilter
              config={{name: 'components', ui: {label: 'hai.properties.origin'}}}
              options={originOptions}
              multi
              value={board.components}
              onChange={(newValue: string[] | null) => changeBoard({...board, components: newValue?.length ? newValue : undefined})}
            />
            <LightSelectFilter
              config={{name: 'includeRetired', ui: {label: 'alert.board.filter.includeRetired'}}}
              options={BOOL_FILTER_OPTIONS}
              maxWidth={'240px'}
              isDefaultValue={!board.includeRetired}
              value={board.includeRetired ? 'TRUE' : 'FALSE'}
              onChange={(newValue) => changeBoard({...board, includeRetired: getSingleBoolFilterValue(newValue) || undefined})}
            />
            <LightSelectFilter
              config={{name: 'includeDeleted', ui: {label: 'alert.board.filter.includeDeleted'}}}
              options={BOOL_FILTER_OPTIONS}
              maxWidth={'240px'}
              isDefaultValue={!board.includeDeleted}
              value={board.includeDeleted ? 'TRUE' : 'FALSE'}
              onChange={(newValue) => changeBoard({...board, includeDeleted: getSingleBoolFilterValue(newValue) || undefined})}
            />
            <LightSelectFilter
              config={{name: 'chartBreakdown', ui: {label: 'alert.board.filter.chartBreakdown'}}}
              options={enumMap[ALERT_BOARD_CHART_BREAKDOWN_ENUM] ?? []}
              maxWidth={'240px'}
              isClearable={false}
              isDefaultValue={(board.chartBreakdown ?? DEFAULT_ALERT_BOARD_CHART_BREAKDOWN) === DEFAULT_ALERT_BOARD_CHART_BREAKDOWN}
              value={board.chartBreakdown ?? DEFAULT_ALERT_BOARD_CHART_BREAKDOWN}
              onChange={(newValue: AlertBoardChartBreakdown | null) => changeChartOption({chartBreakdown: newValue ?? undefined})}
            />
            <LightSelectFilter
              config={{name: 'chartValues', ui: {label: 'department.report.filter.chartvalues'}}}
              options={enumMap[ALERT_BOARD_CHART_VALUES_ENUM] ?? []}
              maxWidth={'240px'}
              isClearable={false}
              isDefaultValue={(board.chartValues ?? DEFAULT_ALERT_BOARD_CHART_VALUES) === DEFAULT_ALERT_BOARD_CHART_VALUES}
              value={board.chartValues ?? DEFAULT_ALERT_BOARD_CHART_VALUES}
              onChange={(newValue: AlertBoardChartValues | null) => changeChartOption({chartValues: newValue ?? undefined})}
            />
            <LightSelectFilter
              config={{name: 'chartType', ui: {label: 'department.report.charts.type'}}}
              options={enumMap[ALERT_BOARD_CHART_TYPE_ENUM] ?? []}
              maxWidth={'240px'}
              isClearable={false}
              isDefaultValue={(board.chartType ?? DEFAULT_ALERT_BOARD_CHART_TYPE) === DEFAULT_ALERT_BOARD_CHART_TYPE}
              value={board.chartType ?? DEFAULT_ALERT_BOARD_CHART_TYPE}
              onChange={(newValue: AlertBoardChartType | null) => changeChartOption({chartType: newValue ?? undefined})}
            />
          </LightFilterPanel>
        </FilterPanelMaximizeWrapper>
        <LightSplitLayout
          initSizes={ALERT_BOARD_CHART_SPLIT_CONFIG.sizes}
          minSizes={ALERT_BOARD_CHART_SPLIT_CONFIG.minSize}
          storageId={ALERT_BOARD_CHART_SPLIT_CONFIG.layoutId}
          isVertical
          gutterSize={SPLIT_GUTTER_SIZE}
          noGrow={[false, false]}
          hidden={[false, false]}
        >
          <div css={muiStyles.boardColumn} data-name={'dl-alert-board'}>
            <AlertBoardKpiStrip kpis={data?.kpis} includeDeleted={Boolean(board.includeDeleted)} />
            <div css={muiStyles.tableCard} data-name={'dl-alert-board-table'}>
              <TableMaximizeWrapper>
                <IconHeaderWithSearch
                  iconName={'bellIcon'}
                  useFixedViewBox={'0 0 448 512'}
                  iconCss={muiStyles.headerIcon}
                  label={'alert.board.definedAlerts'}
                  labelStyle={{fontSize: 19}}
                  childrenWrapperStyle={{gap: 10}}
                >
                  <LightSelectedFilters
                    align='flex-start'
                    marginLeft='8px'
                    hasFilters={hasFilters}
                    handleResetFilters={() => changeBoard(DEFAULT_ALERT_BOARD_FILTERS)}
                    hasSorting={!isEqual(filters.boardSorting, DEFAULT_ALERT_BOARD_SORTING)}
                    handleResetSorting={() => changeFilters({boardSorting: DEFAULT_ALERT_BOARD_SORTING})}
                    hasXlsxExport
                    handleXlsxExport={handleXlsxExport}
                    xlsxExportDisabled={isExporting || floatingPending}
                  />
                  {/* the reports' "Show all": the filled chip in a link, the list's default filter with the board's */}
                  {ALERT_LIST_ROUTE_PATH && (
                    <Link
                      css={muiStyles.showAllLink}
                      tabIndex={0}
                      to={`${ALERT_LIST_ROUTE_PATH}?${buildAlertBoardShowAllQuery(board)}`}
                      data-name={'dl-alert-board-show-all'}
                    >
                      <DlMuiButton
                        type='chip-filled'
                        title='dashboard.showAll'
                        onClick={() => {
                          // noop
                        }}
                      />
                    </Link>
                  )}
                  <span css={muiStyles.maximizeButton} style={{marginRight: tableMaximized ? 0 : filtersScrollbarWidth}}>
                    <MaximizeButton maximized={tableMaximized} toggleMaximized={toggleTableMaximized} />
                  </span>
                </IconHeaderWithSearch>
                <MaterialTableWrapper
                  variant='big'
                  // every alert in one response, the card scrolling them under the sticky header (PDW-413)
                  options={BOARD_TABLE_OPTIONS}
                  useMaxHeight
                  columns={tableColumns}
                  data={definitions ?? []}
                  isLoading={isLoading || floatingPending}
                  onOrderChange={(colIndex, direction) => {
                    const field = tableColumns[colIndex]?.field as string;
                    if (!ALERT_BOARD_SORT_PROPERTIES.includes(field as any)) return;
                    // the table echoes the shown sort back on mount: the default order shown on Weight is not the user's sort
                    if (field === shownSort.field && direction === shownSort.direction) return;
                    changeFilters({boardSorting: {field, direction}});
                  }}
                  controlledSort={shownSort}
                  scrollStorageId='/AlertBoardLayout.scrollXY'
                />
                {/* the count the pagination showed (PDW-416): the strip's count leaves the retired alerts out */}
                {!isLoading && !floatingPending && definitions?.length ? (
                  <div css={muiStyles.resultCount} data-name={'dl-alert-board-result-count'}>
                    <ResultCount
                      count={definitions.length}
                      pageSize={definitions.length}
                      countInPage={definitions.length}
                      activePage={1}
                      isBig
                    />
                  </div>
                ) : null}
              </TableMaximizeWrapper>
            </div>
          </div>
          <div css={muiStyles.chartCard} data-name={'dl-alert-board-chart-card'}>
            <ChartMaximizeWrapper>
              <div css={muiStyles.chartHeader}>
                <div css={muiStyles.chartTitle}>{t('alert.board.chart.title')}</div>
                {/* the card's design padding is 24px against the other cards' 16px, so the button reaches 8px into it */}
                <span
                  css={muiStyles.maximizeButton}
                  style={{marginRight: chartMaximized ? 0 : filtersScrollbarWidth - (CHART_CARD_PADDING_X - CARD_PADDING_X)}}
                >
                  <MaximizeButton maximized={chartMaximized} toggleMaximized={toggleChartMaximized} />
                </span>
              </div>
              <AlertBoardChart
                chart={chart}
                values={board.chartValues}
                chartType={board.chartType}
                isLoading={chartFetching || floatingPending}
              />
            </ChartMaximizeWrapper>
          </div>
        </LightSplitLayout>
      </LightSplitLayout>
    </div>
  );
};

const styles = {
  category: css`
    display: inline-flex;
    /* centred on the text of the row, not sitting on its baseline */
    vertical-align: middle;
    color: var(--dlThemeGray-400);
    &[data-retired='true'] {
      color: var(--dlThemeGray-300);
    }
  `,
  name: css`
    display: inline-block;
    /* the design's 340px, but never wider than the column, else a long name runs under the Weight badge (PDW-410) */
    max-width: min(100%, 340px);
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    vertical-align: middle;
    font-size: 14px;
    font-family: var(--dlFontFamilySemiBold);
    text-decoration: underline;
    text-underline-offset: 2px;
    /* the link stays teal on hover and focus, as the alerts list's names (the global link hover is blue) */
    &,
    &:hover,
    &:focus {
      color: var(--dlTheme-500);
    }
    &[data-retired='true'],
    &[data-retired='true']:hover,
    &[data-retired='true']:focus {
      color: var(--dlThemeGray-400);
      text-decoration: underline line-through;
    }
  `,
  muted: css`
    font-size: 13.5px;
    white-space: nowrap;
    color: var(--dlThemeGray-600);
    &[data-retired='true'] {
      color: var(--dlThemeGray-400);
    }
  `,
  count: css`
    font-size: 14px;
    font-family: var(--dlFontFamilySemiBold);
    color: var(--dlThemeGray-800);
    &[data-retired='true'] {
      color: var(--dlThemeGray-400);
    }
  `,
};

const getStyles: GetStylesFunction = (options) => ({
  contentWrapper: css`
    width: 100%;
    height: 100%;
    padding: 16px 16px 16px 0;
    overflow-y: auto;
  `,
  /* the lower pane never scrolls: the KPI strip keeps its height and the table card takes the rest */
  boardColumn: css`
    display: flex;
    flex-direction: column;
    gap: 16px;
    width: 100%;
    height: 100%;
    min-height: 0;
    overflow: hidden;
    & > [data-name='dl-alert-board-kpis'] {
      flex: 0 0 auto;
    }
  `,
  /*
   * the card fills what the strip leaves, and only the table inside it scrolls; the maximize wrapper inside it is the
   * white card with the padding, and leaves the flow when maximized
   */
  tableCard: css`
    flex: 1 1 0;
    min-height: 0;
    overflow: hidden;
    border-radius: ${options?.theme?.borderRadius};
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.06);
  `,
  /* the design's chart card: the title, and the chart filling the rest of the pane; the padding is the wrapper's */
  chartCard: css`
    min-height: 0;
    overflow: hidden;
    border-radius: ${options?.theme?.borderRadius};
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.06);
  `,
  chartHeader: css`
    flex: 0 0 auto;
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 16px;
    width: 100%;
    margin-bottom: 16px;
    /* the 32px button sits in the title's line height, so the chart keeps the design's distance below the title */
    & > :last-child {
      margin-top: -5px;
      margin-bottom: -5px;
    }
  `,
  maximizeButton: css`
    display: inline-flex;
    flex: 0 0 auto;
  `,
  /* where the pagination's count sat: under the table, on the left */
  resultCount: css`
    flex: 0 0 auto;
    margin-left: var(--dlMarginSmall);
  `,
  chartTitle: css`
    font-size: 17px;
    font-family: var(--dlFontFamilySemiBold);
    color: var(--dlThemeGray-900);
  `,
  headerIcon: css`
    width: 16px;
    height: 16px;
  `,
  // the header row stretches its items, so the chip sat at the top of the stretched link
  showAllLink: css`
    text-decoration: none;
    align-self: center;
  `,
});
